<script setup lang="ts">
import { TESTIMONIALS } from '../../data/testimonialsData'
import { ECMSType, ECMSPage, EHomePageSectionType } from '@/modules/adminModule/constants/pagesCMSconstants'

const googleRatingsStore = useGoogleRatingsStore()

const $t = useI18n().t

defineProps<{
	disableAos?: boolean
}>()
const ratingsForm = reactive({
	stars: '',
	ratings: '',
	ratingsCount: '',
	url: '',
})

watch(
	() => googleRatingsStore.getGoogleRating,
	(v) => {
		if (v) {
			ratingsForm.stars = String(v.stars)
			ratingsForm.ratings = String(v.ratings)
			ratingsForm.ratingsCount = String(v.ratingsCount)
			ratingsForm.url = v.url
		}
	},
	{ immediate: true },
)

const { getLocaleLang } = usei18nService()

const pageSections = inject<Ref<IHomePageSectionModel[]>>('pageSections')!

const fallbackTestimonials = ref()

if (!pageSections) {
	try {
		const type = await ApiService.fetchPageSectionByName(ECMSType.SUBPAGE, ECMSPage.HOME, EHomePageSectionType.TESTIMONIALS)
		if (type)
			fallbackTestimonials.value = type?.translations.find((t: { language: string; content: any }) => t.language === getLocaleLang())?.content
	} catch (error) {
		logger.log(error)
	}
}

const testimonials = computed(() => {
	const section = pageSections?.value?.find((s: IHomePageSectionModel) => s.name === EHomePageSectionType.TESTIMONIALS)
	return (
		section?.translations.find((t: { language: string; content: any }) => t.language === getLocaleLang())?.content ??
		fallbackTestimonials.value ??
		TESTIMONIALS
	)
})
</script>

<template>
	<section class="testimonials">
		<div class="testimonials__container">
			<GlobalImage class="testimonials__image" src="testimonials-image.png" alt="testimonials-image" />
			<div class="testimonials__image-shadow"></div>
			<h2
				:data-aos="disableAos ? undefined : 'fade-up'"
				class="testimonials__title text-display"
				v-html="detectNewline($t('modules.home_page_module.testimonials.opinions'))"></h2>
			<Swiper
				v-if="testimonials"
				:data-aos="disableAos ? undefined : 'fade-up'"
				class="testimonials__swiper"
				:slides-per-view="'auto'"
				:centered-slides="true"
				space-between="140"
				:loop="true"
				:loop-additional-slides="1"
				:watch-slides-progress="true"
				@after-init="
					(swiper) => {
						useTimeoutFn(() => {
							swiper.update()
						}, 400)
					}
				">
				<SwiperSlide v-for="(slide, idx) in testimonials" :key="idx" class="testimonials__swiper__slide">
					<TestimonialsItem
						:index="idx"
						:text="slide.translation_key_text || slide.text"
						:author="slide.translation_key_author || slide.author"
						:stars="slide.stars" />
				</SwiperSlide>
				<TestimonialsSwiperNav />
			</Swiper>
			<div class="testimonials__gradient testimonials__gradient--left"></div>
			<div class="testimonials__gradient testimonials__gradient--right"></div>
		</div>
		<div class="reviews">
			<div v-if="ratingsForm.stars && ratingsForm.stars !== ''" class="google-review">
				<div class="google-review__logo">
					<GlobalIcon name="google-icon" :height="16" />
				</div>
				<p :data-aos="disableAos ? undefined : 'fade-up'" class="google-review__text">
					{{ $t('modules.home_page_module.testimonials.assessment') }} {{ formatRating(ratingsForm.ratings) }}
					{{ $t('modules.home_page_module.testimonials.of') }}
					<a :href="ratingsForm?.url" target="_blank" rel="nofollow" class="google-review__link">
						{{ ratingsForm.ratingsCount }} {{ $t('modules.home_page_module.testimonials.opinions_1') }}
					</a>
					<span v-if="getLocaleLang() === 'pl'"> GOOGLE </span>
				</p>
			</div>
			<LocationTrustpilotReviews variant="bar" />
		</div>
	</section>
</template>

<style lang="scss" scoped src="./Testimonials.scss"></style>
